import { CarouselProductsV2 as CarouselProducts } from '@components/CarouselProducts';
import { RichText } from 'prismic-reactjs';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  IPrismicRichText,
  ISearchProduct,
} from '@monofront/types';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import {
  ICardProductInfos,
  useMerchProductsRecommendations,
} from '@monofront/business/hooks';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { ICarouselProductsProps } from '@components/CarouselProducts/CarouselProductsV2';
import React from 'react';
import { useRecommendationTracking } from '@hooks/tracking/useRecommendationTracking';

interface IMerchRecommendationsCarouselProps {
  title?: IPrismicRichText | string;
  showTitle?: boolean;
  widgetId: string;
  displayTitle?: boolean;
  sku?: string;
  widgetContext?: Omit<AddToShoppingCartContext, 'source'>;
  trackingId?: string;
  isOnCardClickDisabled?: boolean;
  isProductDrawerEnabled?: boolean;
  isViewItemListEnabled?: boolean;
}

type InnerMerchRecommendationsCarouselProps = Omit<
  IMerchRecommendationsCarouselProps,
  'sku'
> & {
  products: Array<ISearchProduct>;
  widgetTitle?: IPrismicRichText;
};

const MerchRecommendationsCarouselContainer: React.FC<
  IMerchRecommendationsCarouselProps & Omit<ICarouselProductsProps, 'products'>
> = ({ widgetId, sku, ...props }) => {
  const { shoppingCart } = useShoppingCart();
  const {
    loading,
    products,
    title: widgetTitle,
  } = useMerchProductsRecommendations({
    widgetId,
    shoppingCart,
    sku,
  });

  return (
    <div className="slice-carousel-recommendations">
      {loading && products.length === 0 && (
        <div className="tw-flex tw-h-[470px] tw-items-center desktop:tw-mb-8 desktop:tw-h-[360px]">
          <HeartLoader />
        </div>
      )}
      <MerchRecommendationsCarouselV2
        {...props}
        widgetId={widgetId}
        products={products}
        widgetTitle={widgetTitle}
      />
    </div>
  );
};

const _MerchRecommendationsCarouselV2: React.FC<
  InnerMerchRecommendationsCarouselProps &
    Omit<ICarouselProductsProps, 'products'>
> = ({
  title,
  showTitle = true,
  widgetContext = {},
  trackingId,
  isOnCardClickDisabled,
  isProductDrawerEnabled = false,
  isViewItemListEnabled = false,
  products,
  widgetTitle,
  widgetId,
  ...carouselProps
}) => {
  // One id per tab: the parent remounts this component on tab change.
  const { ref, sendSelectItemEvent } = useRecommendationTracking({
    trackingId: trackingId || widgetId,
    isViewItemListEnabled,
    products,
  });

  const trackProductInteraction = (
    product: Partial<ICardProductInfos>,
    index: number,
  ) => {
    if (product.sku && product.title) {
      sendSelectItemEvent({
        product: { sku: product.sku, name: product.title, index },
      });
    }
  };

  const context = { ...widgetContext };

  if (widgetContext.widgetName) {
    context.widgetName = `IH-${widgetContext.widgetName}`; // IH prefix for "In House"
  }

  return (
    <>
      {products.length > 0 && (
        <div ref={ref}>
          <CarouselProducts
            products={products}
            headerTitle={
              showTitle ? (
                typeof title === 'string' ? (
                  <h2>{title}</h2>
                ) : (
                  <RichText render={title ?? widgetTitle} />
                )
              ) : undefined
            }
            containerContext={{
              source: AddToShoppingCartSource.RECOMMENDATION,
              ...context,
            }}
            trackingId={`IH-${trackingId}`} // IH prefix for "In House"
            isOnCardClickDisabled={isOnCardClickDisabled}
            isProductDrawerEnabled={isProductDrawerEnabled}
            trackProductInteraction={trackProductInteraction}
            {...carouselProps}
            headerTitlePlainText={
              widgetTitle
                ? RichText.asText(widgetTitle)
                : carouselProps.headerTitlePlainText
            }
          />
        </div>
      )}
      <style jsx>{`
        @media (max-width: 800px) {
          .slice-carousel-recommendations :global(.swiper) {
            margin: var(--negative-padding-layout-base);
            padding: var(--padding-layout-base);
            width: 100vw;
          }
        }
      `}</style>
    </>
  );
};

const MerchRecommendationsCarouselV2 = React.memo(
  _MerchRecommendationsCarouselV2,
);

export default MerchRecommendationsCarouselContainer;
